/**
 * Centralized error handling for cart and product operations
 */

/**
 * Handle cart-related errors
 * @param {Error} error - The error object
 * @param {string} context - Context where error occurred (e.g., 'addToCart', 'fetchPricing')
 * @param {Function} userNotification - Optional callback to show user-friendly message
 */
export function handleCartError(error, context = 'unknown', userNotification = null) {
  console.error(`Cart error in ${context}:`, error);
  
  // Log to analytics if available
  if (typeof gtag === 'function') {
    gtag('event', 'exception', {
      description: `Cart error in ${context}: ${error.message}`,
      fatal: false
    });
  }
  
  // Show user-friendly notification
  if (userNotification) {
    userNotification(getErrorMessage(error, context));
  } else {
    // Fallback to alert if no notification system
    alert(getErrorMessage(error, context));
  }
}

/**
 * Get user-friendly error message
 * @param {Error} error - The error object
 * @param {string} context - Context where error occurred
 * @returns {string} User-friendly error message
 */
function getErrorMessage(error, context) {
  if (error.response) {
    // API error
    if (error.response.status === 403) {
      return 'You do not have permission to perform this action.';
    }
    if (error.response.status === 404) {
      return 'The requested item could not be found.';
    }
    if (error.response.status >= 500) {
      return 'A server error occurred. Please try again later.';
    }
    return 'An error occurred while processing your request.';
  }
  
  if (error.message) {
    return error.message;
  }
  
  return 'An unexpected error occurred. Please try again.';
}

/**
 * Handle API response errors
 * @param {Object} response - API response object
 * @param {string} context - Context where error occurred
 */
export function handleApiResponseError(response, context = 'unknown') {
  if (response.errorMessage) {
    const error = new Error(response.errorMessage);
    handleCartError(error, context);
    return true; // Error was handled
  }
  return false; // No error
}

